import { Outlet, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { KeyedRouteErrorBoundary } from '../RouteErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import { Loader2 } from 'lucide-react';

export function AdminLayout() {
  const { user, profile, loading: authLoading } = useAuth();
  const { availableOrganizations, loading: roleLoading } = useRole();

  // Safety timeout so a slow/failed profile fetch can't keep the admin
  // shell stuck on a spinner forever.
  const [profileWaitTimedOut, setProfileWaitTimedOut] = useState(false);
  useEffect(() => {
    if (!user || profile) {
      setProfileWaitTimedOut(false);
      return;
    }
    const timeout = setTimeout(() => setProfileWaitTimedOut(true), 4000);
    return () => clearTimeout(timeout);
  }, [user, profile]);

  // Wait for auth, role context, AND profile to finish loading before evaluating access.
  // Without the profile check we can briefly see profile=null after a hard reload and
  // incorrectly redirect admin/organizer users back to the landing page.
  const waitingForProfile = user && !profile && !profileWaitTimedOut;
  if (authLoading || roleLoading || waitingForProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Allow access if user is admin, organizer, or has any organizations
  const hasOrganizerAccess = profile?.role === 'admin' || profile?.role === 'organizer' || availableOrganizations.length > 0;
  
  if (!hasOrganizerAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      <AdminSidebar />
      <main className="flex-1 overflow-auto md:ml-0">
        <div className="p-3 sm:p-4 md:p-6 pt-14 sm:pt-16 md:pt-6">
          <KeyedRouteErrorBoundary>
            <Outlet />
          </KeyedRouteErrorBoundary>
        </div>
      </main>
    </div>
  );
}
